import { z } from "zod";

const uuidSchema = z.string().uuid();
const offsetDateTimeSchema = z.string().datetime({ offset: true });
const calendarDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const calendarBookingKindSchema = z.enum(["asap", "scheduled", "unknown"]);
export const calendarStaffRoleSchema = z.enum(["therapist", "rider"]);
export const calendarStaffScheduleKindSchema = z.enum(["duty", "rest_day"]);
export const calendarDispatchLegKindSchema = z.enum(["pickup", "drop_off", "return"]);
export const calendarDispatchLegStatusSchema = z.enum([
  "planned",
  "active",
  "completed",
  "superseded",
]);
export const calendarDispatchPlanStatusSchema = z.enum([
  "planned",
  "active",
  "superseded",
  "completed",
  "cancelled",
]);
export const operationalCalendarInvalidationReasonSchema = z.enum([
  "booking_confirmed",
  "booking_cancelled",
  "booking_changed",
  "session_started",
  "session_ended",
  "rider_unavailable",
  "rider_returned",
  "movement_started",
  "movement_ended",
  "dispatch_delay",
  "route_changed",
  "route_invalid",
  "staff_changed",
  "schedule_published",
]);

/** A service block; no client, address, payment, or coordinate fields. */
export const operationalCalendarBookingSchema = z
  .object({
    bookingId: uuidSchema,
    ref: z.string().min(1),
    bookingGroupId: uuidSchema.nullable(),
    hubId: uuidSchema,
    divisionId: uuidSchema.nullable(),
    /** Generic/assisted legacy rows remain explicit until a source classification is persisted. */
    bookingKind: calendarBookingKindSchema,
    serviceVariantId: uuidSchema,
    serviceName: z.string().min(1),
    durationMinutes: z.number().int().positive(),
    startsAt: offsetDateTimeSchema,
    endsAt: offsetDateTimeSchema,
    status: z.enum([
      "pending",
      "awaiting_therapist",
      "awaiting_rider",
      "dispatched",
      "en_route_to_client",
      "arrived",
      "in_session",
      "completed",
      "cancelled",
    ]),
    therapistId: uuidSchema.nullable(),
    riderId: uuidSchema.nullable(),
    createdAt: offsetDateTimeSchema,
  })
  .strict();

/** A published duty/rest cell used to render staff availability. */
export const operationalCalendarStaffSchema = z
  .object({
    scheduleEntryId: uuidSchema,
    scheduleVersionId: uuidSchema,
    hubId: uuidSchema,
    divisionId: uuidSchema.nullable(),
    weekStart: calendarDateSchema,
    versionNumber: z.number().int().positive(),
    publishedAt: offsetDateTimeSchema,
    staffId: uuidSchema,
    staffRole: calendarStaffRoleSchema,
    staffActive: z.boolean(),
    scheduleDate: calendarDateSchema,
    kind: calendarStaffScheduleKindSchema,
    dutyStart: offsetDateTimeSchema.nullable(),
    dutyEnd: offsetDateTimeSchema.nullable(),
  })
  .strict();

/** A planned leg; actual trip timestamps stay out until each leg has an exact trip relation. */
export const operationalCalendarDispatchSchema = z
  .object({
    dispatchLegId: uuidSchema,
    dispatchPlanId: uuidSchema,
    bookingId: uuidSchema,
    hubId: uuidSchema,
    divisionId: uuidSchema.nullable(),
    planRevision: z.number().int().positive(),
    planStatus: calendarDispatchPlanStatusSchema,
    riderId: uuidSchema,
    kind: calendarDispatchLegKindSchema,
    sequence: z.number().int().positive(),
    fromLocationId: z.string().min(1),
    toLocationId: z.string().min(1),
    plannedStart: offsetDateTimeSchema,
    plannedEnd: offsetDateTimeSchema,
    estimateDistanceKm: z.number().nonnegative().nullable(),
    estimateDurationMin: z.number().int().nonnegative().nullable(),
    estimatedAt: offsetDateTimeSchema.nullable(),
    legStatus: calendarDispatchLegStatusSchema,
    createdAt: offsetDateTimeSchema,
  })
  .strict();

export const operationalCalendarReadModelsSchema = z
  .object({
    staff: z.array(operationalCalendarStaffSchema),
    bookings: z.array(operationalCalendarBookingSchema),
    dispatches: z.array(operationalCalendarDispatchSchema),
  })
  .strict();

/** Realtime carries only this range signal, never a source-table row. */
export const operationalCalendarInvalidationEventSchema = z
  .object({
    id: uuidSchema,
    hub_id: uuidSchema,
    range_start: calendarDateSchema,
    range_end: calendarDateSchema,
    reason: operationalCalendarInvalidationReasonSchema,
    created_at: offsetDateTimeSchema,
  })
  .strict();

export type CalendarBookingKind = z.infer<typeof calendarBookingKindSchema>;
export type CalendarStaffRole = z.infer<typeof calendarStaffRoleSchema>;
export type CalendarStaffScheduleKind = z.infer<typeof calendarStaffScheduleKindSchema>;
export type CalendarDispatchLegKind = z.infer<typeof calendarDispatchLegKindSchema>;
export type CalendarDispatchLegStatus = z.infer<typeof calendarDispatchLegStatusSchema>;
export type CalendarDispatchPlanStatus = z.infer<typeof calendarDispatchPlanStatusSchema>;
export type OperationalCalendarInvalidationReason = z.infer<
  typeof operationalCalendarInvalidationReasonSchema
>;
export type OperationalCalendarBooking = z.infer<typeof operationalCalendarBookingSchema>;
export type OperationalCalendarStaff = z.infer<typeof operationalCalendarStaffSchema>;
export type OperationalCalendarDispatch = z.infer<typeof operationalCalendarDispatchSchema>;
export type OperationalCalendarReadModels = z.infer<typeof operationalCalendarReadModelsSchema>;
export type OperationalCalendarInvalidationEvent = z.infer<
  typeof operationalCalendarInvalidationEventSchema
>;
